import Button from '@mui/material/Button'
import Icon from '@mui/material/Icon'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import React from 'react'

import MenuItem from '@/app/component/atom/menu-item'
import IconText from '@/app/component/layout/icon-text'
import { LocalActionType } from '@/app/module/campaigns/types'
import { ActionOption } from '@/app/module/campaigns/utils/actions/options'

type Props = {
  actionOptions: ActionOption[]
  disabledIndexesFrom: Record<string, number>
  targetIndex: number
  onAddAction: (value: LocalActionType | 'addFromLibrary') => void
}

const AddActionButton: React.FC<Props> = ({ actionOptions, disabledIndexesFrom, targetIndex, onAddAction }) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleAddAction = (value: LocalActionType | 'addFromLibrary') => {
    onAddAction(value)
    handleMenuClose()
  }

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        size="small"
        startIcon={<Icon>add</Icon>}
        endIcon={<Icon>arrow_drop_down</Icon>}
        onClick={handleMenuOpen}
      >
        Add action
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        slotProps={{
          paper: {
            id: 'menu-actions-select',
          },
        }}
        onClose={handleMenuClose}
      >
        <MenuItem divider onClick={() => handleAddAction('addFromLibrary')}>
          <ListItemText
            primary={
              <IconText>
                <Icon>library_add</Icon>
                <span className="select-item-text">Add an action from your library</span>
              </IconText>
            }
            secondary="Add an action you previously saved to your library."
            slotProps={{
              primary: {
                sx: {
                  fontWeight: 600,
                },
              },
            }}
          />
        </MenuItem>
        {actionOptions.map((option) => {
          const isDisabled =
            !!option.disabled ||
            (disabledIndexesFrom[option.value] !== undefined && targetIndex > disabledIndexesFrom[option.value])

          return (
            <MenuItem
              key={option.value}
              disabled={isDisabled}
              tooltip={isDisabled ? option.disabledReason : undefined}
              onClick={isDisabled ? undefined : () => handleAddAction(option.value)}
            >
              <ListItemText
                primary={
                  <IconText>
                    {typeof option.icon === 'string' ? <Icon>{option.icon}</Icon> : option.icon()}
                    <span className="select-item-text">{option.label}</span>
                  </IconText>
                }
                secondary={option.description}
              />
            </MenuItem>
          )
        })}
      </Menu>
    </>
  )
}

export default AddActionButton
